import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "astromeet-admin-token";

// ---- Token storage (server-verified — the PIN itself is NEVER stored) ----
export function getAdminToken() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { token, exp } = JSON.parse(raw);
    if (!token || (exp && Date.now() / 1000 > exp)) {
      localStorage.removeItem(KEY);
      return null;
    }
    return token;
  } catch { return null; }
}

export function saveAdminToken(token, expiresInSeconds) {
  localStorage.setItem(KEY, JSON.stringify({
    token,
    exp: Math.floor(Date.now() / 1000) + Math.max(60, expiresInSeconds || 3600),
  }));
  window.dispatchEvent(new Event("astromeet-admin-changed"));
}

export function clearAdminToken() {
  localStorage.removeItem(KEY);
  window.dispatchEvent(new Event("astromeet-admin-changed"));
}

export function adminAxios() {
  const t = getAdminToken();
  return axios.create({
    baseURL: API,
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
}

export function useAdminUnlocked() {
  const [ok, setOk] = useState(() => !!getAdminToken());
  useEffect(() => {
    const tick = () => setOk(!!getAdminToken());
    const t = setInterval(tick, 60_000);
    const onCustom = () => tick();
    const onStorage = (e) => { if (e.key === KEY) tick(); };
    window.addEventListener("astromeet-admin-changed", onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      clearInterval(t);
      window.removeEventListener("astromeet-admin-changed", onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return ok;
}

export default function AdminGate({ children, subtitle = "Admin Office" }) {
  const ok = useAdminUnlocked();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (ok) return children;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pin.trim()) return;
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/admin/login`, { pin: pin.trim() });
      saveAdminToken(r.data.token, r.data.expires_in);
      setPin("");
    } catch (ex) {
      setErr(ex?.response?.status === 401 ? "Incorrect PIN"
              : (ex?.response?.data?.detail || "Login failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A1428] flex items-center justify-center px-4 py-10">
      <form onSubmit={submit} data-testid="admin-gate"
             className="w-full max-w-sm border border-[#D4AF37]/40 bg-[#0F1A33] p-6 sm:p-8 space-y-5 rounded-sm">
        <div className="flex items-center gap-3">
          <div className="border border-[#D4AF37]/40 p-2 text-[#D4AF37]"><ShieldAlert size={16} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono">
              AstroMeet Telugu · Admin Only
            </div>
            <div className="font-heading text-xl text-[#E9E4D0] leading-tight">{subtitle}</div>
          </div>
        </div>
        <div className="text-xs font-mono text-[#8B99B0] leading-relaxed">
          <Lock size={12} className="inline mr-1 text-[#D4AF37]" />
          Server-verified login. The PIN is never stored in the browser —
          only a signed 24 h session token is kept locally.
        </div>
        <input
          type="password" inputMode="numeric" autoFocus maxLength={30}
          value={pin} onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, ""))}
          data-testid="admin-pin-input" placeholder="••••••••••••••••"
          className="w-full bg-[#0A1428] border border-[#2A3555] focus:border-[#D4AF37] outline-none
                     text-[#E9E4D0] font-mono text-center text-lg tracking-[0.35em] py-3 rounded-sm" />
        {err && <div className="text-red-400 text-xs font-mono" data-testid="admin-pin-error">{err}</div>}
        <Button type="submit" disabled={pin.length < 4 || busy}
                data-testid="admin-pin-submit"
                className="w-full rounded-sm bg-gradient-to-r from-[#B08A3E] to-[#E5C158] text-[#0A1428] hover:brightness-110 font-heading text-base">
          {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Verifying…</>
                : "Unlock Admin Office"}
        </Button>
        <div className="text-center text-[10px] font-mono text-[#8B99B0]">
          Not admin? Return to the <a href="/" className="text-[#D4AF37] hover:underline">public marketplace</a>.
        </div>
      </form>
    </div>
  );
}
